/**
 * A2UI (Agent-to-UI) Action Components
 *
 * Inline React components rendered inside the CopilotKit chat sidebar
 * when the ASA agent calls tools. Designed for the 380px-wide panel
 * using HiveOS design language: rounded-2xl, glass/blur, subtle borders.
 */
import React, { useState, useCallback } from 'react';

// -- Inline SVG micro-icons (no external imports) --

const Spinner: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={`${className} animate-spin`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z" />
  </svg>
);

const Svg: React.FC<{ d: string; className?: string; rule?: boolean }> = ({ d, className = 'w-4 h-4', rule }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor">
    <path fillRule={rule ? 'evenodd' : undefined} clipRule={rule ? 'evenodd' : undefined} d={d} />
  </svg>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <Svg className={className} rule d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
);
const XIcon: React.FC<{ className?: string }> = ({ className }) => (
  <Svg className={className} rule d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" />
);
const PhoneIcon: React.FC<{ className?: string }> = ({ className }) => (
  <Svg className={className} d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
);
const GlobeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className ?? 'w-4 h-4'} viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" clipRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 7 7.5 7A1.5 1.5 0 019 8.5c0 .526-.112 1.025-.316 1.478A6.014 6.014 0 014.332 8.027zM10 16a5.97 5.97 0 003.706-1.294A5.97 5.97 0 0016 10c0-.683-.115-1.338-.327-1.95A5.988 5.988 0 0010 4a5.97 5.97 0 00-4 1.536A6.012 6.012 0 004 10c0 1.074.282 2.084.776 2.958A5.981 5.981 0 0010 16z" />
  </svg>
);
const WhatsAppIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className ?? 'w-4 h-4'} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);
const MessengerIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className ?? 'w-4 h-4'} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.2 5.42 3.15 7.18.16.15.26.36.27.58l.05 1.82c.02.57.6.94 1.12.71l2.03-.89c.17-.08.37-.1.55-.06.9.25 1.85.38 2.83.38 5.64 0 10-4.13 10-9.7C22 6.13 17.64 2 12 2zm5.95 7.53l-2.9 4.6c-.46.73-1.44.92-2.13.4l-2.3-1.73a.6.6 0 00-.72 0l-3.1 2.36c-.42.32-.96-.18-.69-.63l2.9-4.6c.46-.73 1.44-.92 2.13-.4l2.3 1.73a.6.6 0 00.72 0l3.1-2.36c.42-.32.96.18.69.63z" />
  </svg>
);
const ChevronRight: React.FC<{ className?: string }> = ({ className }) => (
  <Svg className={className} rule d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" />
);

// -- Shared constants --

const CARD = 'bg-white/5 dark:bg-slate-800/50 border border-black/5 dark:border-white/5 rounded-2xl p-4';
const STATUS_DOTS: Record<string, string> = {
  ok: 'bg-emerald-500', warning: 'bg-amber-500', error: 'bg-red-500', pending: 'bg-slate-400 animate-pulse',
};

// -- 1. StatusCard --

interface StatusCardProps {
  title: string;
  items: Array<{ label: string; value: string; status?: 'ok' | 'warning' | 'error' | 'pending' }>;
}

export const StatusCard: React.FC<StatusCardProps> = ({ title, items }) => (
  <div className={CARD}>
    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">{title}</h4>
    <div className="space-y-2">
      {items.map((item) => (
        <div key={item.label} className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOTS[item.status ?? 'pending']}`} />
            <span className="text-sm text-slate-600 dark:text-slate-300 truncate">{item.label}</span>
          </div>
          <span className="text-sm font-medium text-slate-900 dark:text-white truncate text-right">{item.value}</span>
        </div>
      ))}
    </div>
  </div>
);

// -- 2. ConfirmationDialog --

interface ConfirmationDialogProps {
  title: string;
  description: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  title, description, onConfirm, onCancel, isLoading = false,
}) => (
  <div className={CARD}>
    <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">{title}</h4>
    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed mb-4">{description}</p>
    <div className="flex items-center gap-2">
      <button
        onClick={onCancel}
        disabled={isLoading}
        className="flex-1 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 bg-transparent border border-black/10 dark:border-white/10 rounded-xl hover:bg-black/5 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
      >Cancel</button>
      <button
        onClick={onConfirm}
        disabled={isLoading}
        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
      >
        {isLoading && <Spinner className="w-3.5 h-3.5" />}
        {isLoading ? 'Working...' : 'Confirm'}
      </button>
    </div>
  </div>
);

// -- 3. ProgressSteps --

interface ProgressStepsProps {
  steps: Array<{ label: string; status: 'pending' | 'active' | 'complete' | 'error' }>;
  title?: string;
}

const StepNode: React.FC<{ status: string }> = ({ status }) => {
  if (status === 'complete')
    return <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shrink-0"><CheckIcon className="w-3 h-3 text-white" /></div>;
  if (status === 'error')
    return <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center shrink-0"><XIcon className="w-3 h-3 text-white" /></div>;
  if (status === 'active')
    return <div className="w-5 h-5 rounded-full bg-blue-600 shrink-0 animate-pulse" />;
  return <div className="w-5 h-5 rounded-full border-2 border-slate-300 dark:border-slate-600 shrink-0" />;
};

const STEP_TEXT: Record<string, string> = {
  active: 'font-semibold text-blue-600 dark:text-blue-400',
  complete: 'text-emerald-600 dark:text-emerald-400',
  error: 'text-red-500 dark:text-red-400',
  pending: 'text-slate-400 dark:text-slate-500',
};

export const ProgressSteps: React.FC<ProgressStepsProps> = ({ steps, title }) => (
  <div className={CARD}>
    {title && <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">{title}</h4>}
    <div className="space-y-0">
      {steps.map((step, i) => (
        <div key={`${step.label}-${i}`} className="flex gap-3">
          <div className="flex flex-col items-center">
            <StepNode status={step.status} />
            {i < steps.length - 1 && <div className="w-0.5 flex-1 min-h-[16px] bg-slate-200 dark:bg-slate-700" />}
          </div>
          <div className={`pb-4 text-sm ${STEP_TEXT[step.status]}`}>{step.label}</div>
        </div>
      ))}
    </div>
  </div>
);

// -- 4. ChannelStatusCard --

interface ChannelStatusCardProps {
  channels: Array<{ name: string; type: string; connected: boolean; phone?: string; details?: string }>;
}

const channelIcon = (type: string): React.ReactNode => {
  const t = type.toLowerCase();
  if (t === 'whatsapp') return <WhatsAppIcon />;
  if (t === 'messenger' || t === 'instagram_dm' || t === 'instagram') return <MessengerIcon />;
  if (t === 'voice' || t === 'sms') return <PhoneIcon />;
  return <GlobeIcon />;
};

export const ChannelStatusCard: React.FC<ChannelStatusCardProps> = ({ channels }) => (
  <div className={CARD}>
    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">Channels</h4>
    <div className="space-y-2.5">
      {channels.map((ch) => (
        <div key={ch.name} className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
            ch.connected ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-slate-100 dark:bg-slate-700/50 text-slate-400 dark:text-slate-500'
          }`}>{channelIcon(ch.type)}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-900 dark:text-white truncate">{ch.name}</span>
              <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md ${
                ch.connected ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-slate-100 dark:bg-slate-700/50 text-slate-400 dark:text-slate-500'
              }`}>{ch.connected ? 'Live' : 'Off'}</span>
            </div>
            {(ch.phone || ch.details) && (
              <span className="text-xs text-slate-400 dark:text-slate-500 truncate block">{ch.phone ?? ch.details}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// -- 5. SetupActionButton --

interface SetupActionButtonProps {
  label: string;
  description?: string;
  icon?: string;
  onClick: () => void;
  isLoading?: boolean;
  isComplete?: boolean;
}

export const SetupActionButton: React.FC<SetupActionButtonProps> = ({
  label, description, icon, onClick, isLoading = false, isComplete = false,
}) => (
  <button
    onClick={onClick}
    disabled={isLoading || isComplete}
    className={`w-full text-left ${CARD} transition-all group ${
      isComplete ? 'bg-emerald-500/5 dark:bg-emerald-500/10 border-emerald-500/20'
        : isLoading ? 'opacity-80 cursor-wait'
        : 'hover:bg-white/10 dark:hover:bg-slate-800/70 hover:border-blue-500/20 cursor-pointer'
    }`}
  >
    <div className="flex items-center gap-3">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 text-lg ${
        isComplete ? 'bg-emerald-500/10 text-emerald-500' : 'bg-blue-600/10 text-blue-600 dark:text-blue-400'
      }`}>
        {isLoading ? <Spinner className="w-4 h-4" /> : isComplete ? <CheckIcon className="w-5 h-5" /> : <span>{icon ?? '+'}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <span className={`text-sm font-medium block truncate ${isComplete ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-900 dark:text-white'}`}>{label}</span>
        {description && <span className="text-xs text-slate-500 dark:text-slate-400 block truncate">{description}</span>}
      </div>
      {!isComplete && !isLoading && (
        <ChevronRight className="w-4 h-4 text-slate-300 dark:text-slate-600 group-hover:text-blue-500 transition-colors shrink-0" />
      )}
    </div>
  </button>
);

// -- 6. InlineForm --

interface InlineFormField {
  name: string;
  label: string;
  type: 'text' | 'number' | 'select';
  options?: string[];
  required?: boolean;
}

interface InlineFormProps {
  title: string;
  fields: InlineFormField[];
  onSubmit: (data: Record<string, string>) => void;
  isLoading?: boolean;
}

const INPUT_CLASS =
  'w-full px-3 py-2 text-sm rounded-xl bg-white/5 dark:bg-slate-900/50 border border-black/10 dark:border-white/10 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all';

export const InlineForm: React.FC<InlineFormProps> = ({ title, fields, onSubmit, isLoading = false }) => {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const f of fields) init[f.name] = f.type === 'select' && f.options?.length ? f.options[0] : '';
    return init;
  });

  const handleChange = useCallback((name: string, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(values);
  }, [onSubmit, values]);

  const allRequiredFilled = fields.filter((f) => f.required).every((f) => values[f.name]?.trim());

  return (
    <form onSubmit={handleSubmit} className={CARD}>
      <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">{title}</h4>
      <div className="space-y-3">
        {fields.map((field) => (
          <div key={field.name}>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              {field.label}{field.required && <span className="text-red-400 ml-0.5">*</span>}
            </label>
            {field.type === 'select' && field.options ? (
              <select value={values[field.name] ?? ''} onChange={(e) => handleChange(field.name, e.target.value)} disabled={isLoading} className={INPUT_CLASS}>
                {field.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            ) : (
              <input type={field.type} value={values[field.name] ?? ''} onChange={(e) => handleChange(field.name, e.target.value)} disabled={isLoading} placeholder={field.label} className={INPUT_CLASS} />
            )}
          </div>
        ))}
      </div>
      <button
        type="submit"
        disabled={isLoading || !allRequiredFilled}
        className="mt-4 w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {isLoading && <Spinner className="w-3.5 h-3.5" />}
        {isLoading ? 'Submitting...' : 'Submit'}
      </button>
    </form>
  );
};
